import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import queryString from 'query-string';
import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {ApiFeature, ApiFeatureData} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {NotesActions, NotesState} from 'app/stores/NotesStore';
import {isInternalOrgMemberViewingAnyOrg, useUserInfo} from 'app/utils/featureFlags';
import * as featureUtils from 'app/utils/featureUtils';
import {ANY_TRUECOLOR_HIGH_RES} from 'app/utils/layers';
import {
  getLayerIdFromGroupedHighResDataLayerKey,
  isLayerKeyGroupedHighResData,
  isLayerKeyHighResData,
  isLayerKeyHighResTruecolor,
  makeGroupedHighResDataLayerKey,
  parseLayerKey,
} from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';
import * as routeUtils from 'app/utils/routeUtils';

import {FeatureSelection, useChangeSelection} from '../ProjectDashboardSwitch';
import {MapStateDispatch} from './MapStateProvider';

const OFFSET_NEXT = 1;
const OFFSET_PREVIOUS = -1;

const Hotkeys: React.FunctionComponent<
  React.PropsWithChildren<{
    organizationId: string;
    selectedProjectId: string;
    selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>;
    selectedFeatures: I.Set<I.ImmutableOf<ApiFeature>>;
    imageRefs: mapUtils.MapImageRefs;
    featureData: I.ImmutableOf<ApiFeatureData[]> | null;
    mapStateDispatch: MapStateDispatch;
    features: I.ImmutableOf<ApiFeature[]>;
    notesState: NotesState;
    notesActions: NotesActions;
    blockedHotkeys?: Record<string, boolean>;
  }>
> = ({
  organizationId,
  selectedProjectId,
  selectedFeatureCollection,
  selectedFeatures,
  imageRefs,
  mapStateDispatch: setMapState,
  featureData,
  features,
  notesState,
  notesActions,
  blockedHotkeys,
}) => {
  const [_organization, profile] = useUserInfo();
  const changeSelection = useChangeSelection();
  const navigate = useNavigate();
  const location = useLocation();
  const [toggledImageRefs, setToggledImageRefs] = React.useState<mapUtils.MapImageRefs | undefined>(
    undefined
  );

  const changeImageRef = React.useCallback(
    (nextImageRefs: mapUtils.MapImageRef | undefined) => {
      if (!nextImageRefs) {
        return;
      }
      setMapState({type: 'SET_IMAGE_REFS', imageRefs: [nextImageRefs]});
    },
    [setMapState]
  );

  const toggleTruecolor = React.useCallback(
    (activeLayerKey: string, imageRefs: mapUtils.MapImageRefs) => {
      // we do not want to swap to Truecolor if they are already viewing Truecolor
      if (!activeLayerKey.includes('high-res-truecolor')) {
        // store the imageRefs the user was previously viewing
        setToggledImageRefs(imageRefs);
        // toggle truecolor ON
        setMapState({type: 'SET_LAYER', layerKey: ANY_TRUECOLOR_HIGH_RES});
      } else if (toggledImageRefs) {
        // toggle truecolor OFF
        setMapState({
          type: 'SET_IMAGE_REFS',
          imageRefs: toggledImageRefs,
        });
        // reset stored imageRefs to undefined when toggle set to "truecolor off", otherwise
        // if user changes maps when in "ON" mode, we get the wrong map when they toggle back.
        setToggledImageRefs(undefined);
      }
    },
    [toggledImageRefs]
  );

  const activeLayerKey = React.useMemo(() => imageRefs[0].layerKey, [imageRefs[0].layerKey]);
  const currentCursor = React.useMemo(() => imageRefs[0].cursor, [imageRefs[0].cursor]);
  const cursorKey = featureUtils.getCursorKeyForLayerKey(selectedFeatureCollection, activeLayerKey);

  // The second compare-mode pane, if any (imageRefs is sorted ascending, so
  // [0] is the earlier scene and [1] the later one).
  const compareImageRef = imageRefs[1] ?? null;

  // Internal-only: toggle the scene(s) currently on the map in/out of the URL as
  // the same `sceneDate`/`sceneLayerKey` params the order imagery modal's
  // copy-link uses, so an internal user can grab a shareable link to exactly
  // what's on screen. In compare mode both panes are captured as multi-valued
  // params ([earlier, later]); the URL handling in MonitorProjectView reads the
  // first value and restores both panes in compare mode. Like the feature-ID
  // toggle it's bound to, a second Alt+I press strips the params back out,
  // leaving any other params untouched. Self-contained.
  const toggleSceneInUrl = React.useCallback(() => {
    if (!currentCursor) {
      return;
    }
    const params = queryString.parse(location.search);
    const locationState =
      location.state && typeof location.state === 'object' ? location.state : {};
    let isMirroringSceneInUrl = true;
    if (params[routeUtils.SCENE_DATE_QUERY_PARAM]) {
      delete params[routeUtils.SCENE_DATE_QUERY_PARAM];
      delete params[routeUtils.SCENE_LAYER_KEY_QUERY_PARAM];
      isMirroringSceneInUrl = false;
    } else if (compareImageRef?.cursor) {
      params[routeUtils.SCENE_DATE_QUERY_PARAM] = [currentCursor, compareImageRef.cursor];
      params[routeUtils.SCENE_LAYER_KEY_QUERY_PARAM] = [activeLayerKey, compareImageRef.layerKey];
    } else {
      params[routeUtils.SCENE_DATE_QUERY_PARAM] = currentCursor;
      params[routeUtils.SCENE_LAYER_KEY_QUERY_PARAM] = activeLayerKey;
    }
    const search = queryString.stringify(params);
    navigate(`${location.pathname}${search ? `?${search}` : ''}${location.hash}`, {
      replace: true,
      state: {
        ...locationState,
        [routeUtils.SCENE_URL_MIRRORING_STATE_KEY]: isMirroringSceneInUrl,
      },
    });
  }, [currentCursor, activeLayerKey, compareImageRef, location, navigate]);

  // if activeLayerKey is a specific high-res data layer (e.g., "AIRBUS-PNEO-MULTISPECTRAL_NDVI"),
  // convert it to the corresponding grouped key (e.g., "ANY_NDVI_high-res-data") for filtering
  // so we include all sources (NAIP, AIRBUS, etc.) in the processed data
  const layerKeyForFiltering = React.useMemo(() => {
    if (isLayerKeyHighResData(activeLayerKey) && !isLayerKeyGroupedHighResData(activeLayerKey)) {
      const parsed = parseLayerKey(activeLayerKey);
      return makeGroupedHighResDataLayerKey(parsed.layerId);
    }
    return activeLayerKey;
  }, [activeLayerKey]);

  const processedFeatureData = React.useMemo(() => {
    const filteredData = featureData
      ? featureData.filter(featureUtils.makeLayerKeyPredicate(layerKeyForFiltering)).toList()
      : I.List([]);
    return featureUtils.getProcessedFeatureData(filteredData, layerKeyForFiltering);
  }, [featureData, layerKeyForFiltering]);

  const unrolledFeatureData = React.useMemo(
    () => featureUtils.unrollFeatureDataBySource(processedFeatureData, activeLayerKey),
    [processedFeatureData, activeLayerKey]
  );

  const findHighResTruecolor = isLayerKeyHighResTruecolor(activeLayerKey);

  const isViewingAnyGroupedHighResData =
    isLayerKeyGroupedHighResData(activeLayerKey) || // eg ANY_NAIP_high-res-data
    (isLayerKeyHighResData(activeLayerKey) && !isLayerKeyGroupedHighResData(activeLayerKey)); // eg NAIP_NDVI

  // find the layerId of the high-res data layer we are viewing
  const groupedHighResDataLayerId = React.useMemo(() => {
    if (isLayerKeyGroupedHighResData(activeLayerKey)) {
      return getLayerIdFromGroupedHighResDataLayerKey(activeLayerKey);
    } else if (isLayerKeyHighResData(activeLayerKey)) {
      return parseLayerKey(activeLayerKey).layerId;
    }
    return null;
  }, [activeLayerKey]);

  // construct the list of imageRefs we have available in the dropdown so we can nav between them
  const imageRefsList: I.List<mapUtils.MapImageRef> = React.useMemo(
    () =>
      I.List(
        // only include imageRefs that we have templateUrls for, otherwise we might
        // include scenes we don't have tiles for (including unordered scenes)
        unrolledFeatureData
          .toSeq()
          .filter(
            (d) =>
              d!.get('measurements') &&
              d!.getIn(['images', 'templateUrls']).size > 0 &&
              (findHighResTruecolor
                ? !!d!.get('types').find((t) => isLayerKeyHighResTruecolor(t!))
                : isViewingAnyGroupedHighResData && groupedHighResDataLayerId
                  ? !!d!.get('types').find((t) => {
                      const parsed = parseLayerKey(t!);
                      return (
                        parsed.layerId === groupedHighResDataLayerId && isLayerKeyHighResData(t!)
                      );
                    })
                  : d!.get('types').includes(activeLayerKey))
          )
          .map((d) => {
            return {
              cursor: d!.get(cursorKey as any),
              //because we unrolled our featureData by source, we can assume the only types remaining on the
              //featureDatum are associated with the source (eg NAIP) and layer (eg high-res-truecolor).
              layerKey: d!.get('types').first(),
            };
          })
          .sortBy((imageRef) => imageRef!.cursor, featureUtils.alphanumericSort)
      ),
    [
      activeLayerKey,
      cursorKey,
      findHighResTruecolor,
      isViewingAnyGroupedHighResData,
      groupedHighResDataLayerId,
      unrolledFeatureData,
    ]
  );

  const hotkeys: B.HotkeyConfig[] = [];

  if (selectedFeatures.size > 0) {
    // Sort here matches the sorting behavior in FeatureList
    const sortedFeatures = features
      .sortBy((f) => f!.getIn(['properties', 'name']), featureUtils.alphanumericSort)
      .toList();

    hotkeys.push(
      {
        global: true,
        combo: 'down',
        label: 'Select next property location',
        disabled: !!blockedHotkeys?.['down'],
        onKeyDown: () => {
          const selection: FeatureSelection = {
            organizationId,
            projectId: selectedProjectId,
          };

          if (selectedFeatures.size > 1) {
            selection.featureIds = I.Set([selectedFeatures.first().get('id')]);
          } else {
            const selectedFeatureIndex = sortedFeatures.findIndex(
              (f) => f!.get('id') === selectedFeatures.first().get('id')
            );
            const nextSelectedFeatureIndex = selectedFeatureIndex + 1;
            if (nextSelectedFeatureIndex >= sortedFeatures.size) {
              return;
            }

            const nextFeature = sortedFeatures.get(nextSelectedFeatureIndex);
            selection.featureIds = I.Set([nextFeature.getIn(['properties', 'lensId'])!]);
          }
          changeSelection(selection);
        },
      },
      {
        global: true,
        combo: 'up',
        disabled: !!blockedHotkeys?.['up'],
        label: 'Select previous property location',
        onKeyDown: () => {
          const selection: FeatureSelection = {
            organizationId,
            projectId: selectedProjectId,
          };

          if (selectedFeatures.size > 1) {
            selection.featureIds = I.Set([selectedFeatures.last().get('id')]);
          } else {
            const selectedFeatureIndex = sortedFeatures.findIndex(
              (f) => f!.get('id') === selectedFeatures.first().get('id')
            );
            const nextSelectedFeatureIndex = selectedFeatureIndex - 1;
            if (nextSelectedFeatureIndex < 0) {
              return;
            }

            const nextFeature = sortedFeatures.get(nextSelectedFeatureIndex);
            selection.featureIds = I.Set([nextFeature.getIn(['properties', 'lensId'])!]);
          }
          changeSelection(selection);
        },
      }
    );
  }

  if (
    selectedFeatureCollection &&
    selectedFeatures.size == 1 &&
    unrolledFeatureData.size &&
    imageRefs.length === 1 &&
    currentCursor !== null
  ) {
    const nextImageRef = featureUtils.getImageRefViaOffset(
      activeLayerKey,
      imageRefsList,
      currentCursor,
      OFFSET_NEXT
    );

    const prevImageRef = featureUtils.getImageRefViaOffset(
      activeLayerKey,
      imageRefsList,
      currentCursor,
      OFFSET_PREVIOUS
    );

    hotkeys.push(
      {
        global: true,
        combo: 'left',
        disabled: !!blockedHotkeys?.['left'],
        label: 'Select previous date for selected layer',
        onKeyDown: (ev) => {
          if (!ev.defaultPrevented) {
            changeImageRef(prevImageRef);
          }
        },
      },
      {
        global: true,
        combo: 'right',
        disabled: !!blockedHotkeys?.['right'],
        label: 'Select next date for selected layer',
        onKeyDown: (ev) => {
          if (!ev.defaultPrevented) {
            changeImageRef(nextImageRef);
          }
        },
      },
      {
        global: true,
        combo: 'shift + t',
        disabled: !!blockedHotkeys?.['shift + t'],
        label: 'Toggle the most recent Truecolor image on/off',
        onKeyDown: () => toggleTruecolor(activeLayerKey, imageRefs),
      }
    );
  }

  hotkeys.push(
    {
      global: true,
      combo: 'alt',
      label: 'Show filled overlays (when zoomed in)',
      disabled: !!blockedHotkeys?.['alt'],
    },
    {
      global: true,
      combo: 'alt + left click',
      label: 'Copy coordinate from map at cursor location',
      disabled: !!blockedHotkeys?.['alt + left click'],
    }
  );

  if (selectedFeatures.size === 1) {
    hotkeys.push({
      global: true,
      combo: 'n',
      label: 'Add a note',
      disabled: !notesState.mayCreateNotes || !!blockedHotkeys?.['n'],
      onKeyDown: (ev) => {
        // A form that's already open (from any entry point) keeps its draft.
        if (ev.defaultPrevented || notesState.noteComposer) {
          return;
        }
        ev.preventDefault();
        notesActions.startComposingNote({source: 'hotkey'});
      },
    });

    hotkeys.push({
      global: true,
      combo: 'alt + i',
      label: 'Toggle feature ID in property name, and the current scene(s) in the URL',
      disabled: !!blockedHotkeys?.['alt + i'],
      onKeyDown: (ev) => {
        if (!ev.defaultPrevented && isInternalOrgMemberViewingAnyOrg(profile)) {
          setMapState({type: 'TOGGLE_SHOW_FEATURE_ID_IN_TITLE'});
          toggleSceneInUrl();
        }
      },
    });
  }

  return (
    <B.HotkeysTarget2 hotkeys={hotkeys}>
      <></>
    </B.HotkeysTarget2>
  );
};

export default Hotkeys;
